import React, { useId } from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/utils";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  count?: number;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className,
}: SegmentedControlProps<T>) {
  const indicatorId = useId();

  return (
    <div role="tablist" className={cn("flex rounded-xl bg-gray-100 p-1", className)}>
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(option.value)}
            className={cn(
              "relative flex flex-1 items-center justify-center gap-1.5 rounded-lg border-0 bg-transparent px-2 py-1.5 text-xs transition-colors [-webkit-tap-highlight-color:transparent]",
              isActive ? "font-semibold text-gray-900" : "font-medium text-gray-500",
            )}
          >
            {isActive && (
              <motion.span
                layoutId={`segmented-${indicatorId}`}
                className="absolute inset-0 rounded-lg bg-white shadow-sm"
                transition={{ type: "spring", stiffness: 500, damping: 38 }}
              />
            )}
            <span className="relative">{option.label}</span>
            {option.count !== undefined && (
              <span
                className={cn(
                  "relative rounded-full px-1.5 text-[10px] leading-4",
                  isActive ? "bg-primary-50 text-primary-700" : "bg-gray-200 text-gray-500",
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
